// AGC: OGB Token contract interaction hooks using wagmi
// AGC: Provides read and write operations for OGB token contract
// AGC: Automatically handles caching and revalidation

import { useEffect, useMemo, useRef } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { formatEther } from 'viem'
import { getContractAddresses } from '../config/networks'
import { useCanClaimAirdropV2 } from './useAirdropManagerV2'

const STATUS_REFETCH_INTERVAL_MS = 20000
const DEFAULT_QUERY_OPTIONS = {
  gcTime: 60000,
  staleTime: 15000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const

// AGC: OGB Token ABI - Essential functions for game operations
export const OGB_TOKEN_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'getPlayerStatus',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [
      { name: 'balance', type: 'uint256' },
      { name: 'canClaim', type: 'bool' },
      { name: 'canBattle', type: 'bool' },
    ],
  },
  {
    name: 'hasReceivedAirdrop',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'claimAirdrop',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: [],
  },
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'BATTLE_COST',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'AIRDROP_AMOUNT',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

/**
 * AGC: Read OGB token balance for an address
 * AGC: Returns balance in OGB units (formatted from wei)
 */
export function useOGBBalance(address?: `0x${string}`) {
  const contracts = getContractAddresses()
  
  const { data, isLoading, error, refetch } = useReadContract({
    address: contracts.OGB_TOKEN as `0x${string}`,
    abi: OGB_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    watch: false,
    query: {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!address && contracts.OGB_TOKEN !== '0x0000000000000000000000000000000000000000',
    },
  })

  return {
    balance: data ? Number(formatEther(data as bigint)) : 0,
    balanceWei: data as bigint | undefined,
    isLoading,
    error,
    refetch,
  }
}

/**
 * AGC: Check if player can claim airdrop
 */
export function useCanClaimAirdrop(address?: `0x${string}`) {
  const contracts = getContractAddresses()
  
  const { data, isLoading, error } = useReadContract({
    address: contracts.OGB_TOKEN as `0x${string}`,
    abi: OGB_TOKEN_ABI,
    functionName: 'hasReceivedAirdrop',
    args: address ? [address] : undefined,
    watch: false,
    query: {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!address && contracts.OGB_TOKEN !== '0x0000000000000000000000000000000000000000',
    },
  })

  return {
    canClaim: data !== undefined ? !(data as boolean) : false,
    hasReceivedAirdrop: data as boolean | undefined,
    isLoading,
    error,
  }
}

/**
 * AGC: Get complete player status (balance + claim eligibility + battle readiness)
 * AGC: Now checks both OGB_TOKEN (old system) and AirdropManagerV2 (new system with rounds)
 */
export function usePlayerStatus(address?: `0x${string}`) {
  const contracts = getContractAddresses()
  const lastStatusRef = useRef<readonly [bigint, boolean, boolean] | null>(null)
  
  // AGC: Check AirdropManagerV2 for current round (allows re-claiming in new rounds)
  const { canClaim: canClaimV2, isLoading: isLoadingV2 } = useCanClaimAirdropV2(address)
  
  const { data, isLoading, error, refetch } = useReadContract({
    address: contracts.OGB_TOKEN as `0x${string}`,
    abi: OGB_TOKEN_ABI,
    functionName: 'getPlayerStatus',
    args: address ? [address] : undefined,
    watch: false,
    query: {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!address && contracts.OGB_TOKEN !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Persist the last successful status so transient RPC errors don't zero out the UI
  useEffect(() => {
    if (Array.isArray(data) && data.length === 3) {
      lastStatusRef.current = data as readonly [bigint, boolean, boolean]
    }
  }, [data])

  useEffect(() => {
    // AGC: Poll at a fixed interval to keep UI fresh without per-block RPC spam
    if (!address || typeof window === 'undefined') {
      return
    }

    const intervalId = window.setInterval(() => {
      refetch().catch(() => {
        // AGC: Ignore transient RPC errors; cached status will continue to display
      })
    }, STATUS_REFETCH_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [address, refetch])

  const statusTuple = useMemo(() => {
    if (Array.isArray(data) && data.length === 3) {
      return data as readonly [bigint, boolean, boolean]
    }
    if (lastStatusRef.current) {
      return lastStatusRef.current
    }
    return [0n, false, false] as const
  }, [data])

  const [balance, canClaimOld, canBattle] = statusTuple

  // AGC: Combine checks - can claim if either old system OR new system allows it
  // AGC: Priority to AirdropManagerV2 (new system with rounds)
  const canClaim = canClaimV2 || (canClaimOld as boolean)

  return {
    balance: Number(formatEther(balance as bigint)),
    balanceWei: balance as bigint,
    canClaim: canClaim,
    canBattle: canBattle as boolean,
    isLoading: isLoading || isLoadingV2,
    error,
    refetch,
  }
}

/**
 * AGC: Claim welcome airdrop (1000 OGB tokens)
 * AGC: Returns transaction hash and status
 */
export function useClaimAirdrop() {
  const { address } = useAccount()
  const contracts = getContractAddresses()
  const { writeContractAsync, data: hash, isPending, error } = useWriteContract()
  
  // AGC: Wait for transaction receipt
  const { data: receipt, isLoading: isConfirming } = useWaitForTransactionReceipt({
    hash,
  })

  /**
   * AGC: Execute airdrop claim
   */
  const claimAirdrop = async () => {
    if (!address) {
      throw new Error('Wallet not connected')
    }

    const txHash = await writeContractAsync({
      address: contracts.OGB_TOKEN as `0x${string}`,
      abi: OGB_TOKEN_ABI,
      functionName: 'claimAirdrop',
    })

    return txHash
  }

  return {
    claimAirdrop,
    hash,
    receipt,
    isPending,
    isConfirming,
    isSuccess: receipt?.status === 'success',
    error,
  }
}

/**
 * AGC: Get battle cost constant from contract
 */
export function useBattleCost() {
  const contracts = getContractAddresses()
  
  const { data, isLoading } = useReadContract({
    address: contracts.OGB_TOKEN as `0x${string}`,
    abi: OGB_TOKEN_ABI,
    functionName: 'BATTLE_COST',
    watch: false,
    query: {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: contracts.OGB_TOKEN !== '0x0000000000000000000000000000000000000000',
    },
  })

  return {
    battleCost: data ? Number(formatEther(data as bigint)) : 50,
    battleCostWei: data as bigint | undefined,
    isLoading,
  }
}

/**
 * AGC: Get airdrop amount constant from contract
 */
export function useAirdropAmount() {
  const contracts = getContractAddresses()
  
  const { data, isLoading } = useReadContract({
    address: contracts.OGB_TOKEN as `0x${string}`,
    abi: OGB_TOKEN_ABI,
    functionName: 'AIRDROP_AMOUNT',
    watch: false,
    query: {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: contracts.OGB_TOKEN !== '0x0000000000000000000000000000000000000000',
    },
  })

  return {
    airdropAmount: data ? Number(formatEther(data as bigint)) : 1000,
    airdropAmountWei: data as bigint | undefined,
    isLoading,
  }
}

